/**
 * @file   src\Pages\MultilocationUsageReports.tsx
 * @brief  Multi Location Usage Reports page for each carwash.
 * @date   JUL, 2024
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */

import {
  Container,
  Table,
  Breadcrumb,
  useNavigate,
  useEffect,
  useOutletContext,
  Dropdown,
  useState,
  Button,
  useParams,
  Pagination,
  React,
  Link,
  Row,
  Col,
  Modal,
} from "../utils/thirdpartyComponents";
import { userType, WashType } from "../utils/enums";
import { formValueInterface, IMultiLocationItem, IMultiLocationUsage, WashRecord } from "../interfaces/generalInterface";
import { pageURLs } from "../utils/pageUrls";
import { AppContextType } from "../interfaces/generalInterface";
import { ChangeEvent } from "react";
import axios from "../config/axiosConfig";
import { CompanyApi, LocationApi, MultiLocationApi, RevenueApi } from "../utils/apiUrls";
import SmallLoader from "../components/SmallLoader";
import CWSelect from "../components/CWSelect";
import { MessageToaster } from "../utils/toastUtil";
import { LocationItem, Option } from "../interfaces/subscriptionInterface";
import { MONTHS_OPTIONS } from "../utils/constants";

const MultilocationUsageReports = (props: any) => {
  // Access the context
  const { updateData } = useOutletContext<AppContextType>();
  const carwash_owner =
    props.userType === userType["CARWASH-OWNER"] ? true : false;

  const navigate = useNavigate();

  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedOption, setSelectedOption] = useState("month");
  const [temporaryOption, setTemporaryOption] = useState(selectedOption);
  const params = useParams();
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [washRecords, setWashRecords] = useState<IMultiLocationUsage[]>([]);
  const [showViewPopup, setShowViewPopup] = useState(false);
  const [selectedItem, setSelectedItem] = useState<IMultiLocationItem[]>([]);
  const [selectedType,setSelectedType] = useState(null)
    const [filters, setFilters] = useState({
    locationId: "",
    washType: "",
    month: "",
    year: "",
  });
  const [oweToList, setOweToList] = useState([]);
  const [oweFromList, setOweFromList] = useState([]);

  const [showPopup, setShowPopup] = useState(false);

  const today = new Date();
  const toaster = new MessageToaster();

  const [formValues, setFormValues] = useState<formValueInterface>({
    month: today.getMonth() + 1,
    year: today.getFullYear(),
    fromDate: null,
    toDate: null,
    dateFilterType: "month",
    defaultFilter: true,
    dateFilterChanged: false,
  });

  // State to company details
  const [company, setCompany] = useState({
    companyName: "",
    address1: "",
    address2: "",
    city: "",
    state: "",
    zipCode: "",
  });

  // State to handle locations given in dropdown
  const [locations, setLocations] = useState<Option[]>([]);
  const pageSize =10


  const setDropdown = () => {
    setTemporaryOption(selectedOption); // Reset temporary selection to confirmed option

    setShowDropdown(!showDropdown);
  };

  // To display heading
  useEffect(() => {
    getCompany();
    getCompanyAddress();
  }, []);

  useEffect(() => {
    handleUpdate();
  }, [company]);



   useEffect(() => {
     if (filters.locationId && filters.washType && filters.month && filters.year) {
    getMultiLocationReport(currentPage);
  }
  }, [currentPage]);

  const getCompanyAddress = async () => {
    try {
      const id = params.id ? parseInt(params.id) : 0;
      const { data } = carwash_owner
        ? await axios.get(CompanyApi.CARWASH_OWNER_COMPANY_ADDRESS)
        : await axios.get(CompanyApi.GET_COMPANY_ADDRESS + id);
      if (data?.error === 0) {
        setCompany(data.data);
      }
    } catch (error: any) {
      throw new Error(error.response?.data?.message || error.message);
    }
  };

  // Update the data
  const handleUpdate = () => {
    updateData({
      heading: "Multi Location Usage Reports",
      url: carwash_owner
        ? ""
        : `/superadmin/${pageURLs.carwash_locations}/${params.id}`,
      showBackButton:
        props.userType === userType["CARWASH-OWNER"] ? false : true,
      showMainMenu: true,
      activeLink:
        props.userType === userType["CARWASH-OWNER"]
          ? "reports"
          : "manage-car-wash",
    });
  };

 const handleFilterChange = (name: any, value: string) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const getMultiLocationReport = async (pageNo = 1) => {
  try {
    setLoading(true);

    const params = new URLSearchParams({
      pageNo: pageNo.toString(),
      pageSize: pageSize.toString(),
    });

    // Append only when values are present
    if (filters.locationId) params.append("locationId", filters.locationId);
    if (filters.washType) params.append("washType", filters.washType);
    if (filters.month) params.append("month", filters.month);
    if (filters.year) params.append("year", filters.year);
    if(filters.locationId!='' &&  filters.washType!=''   && filters.month!='' && filters.year!='')
    {
    const url = `${MultiLocationApi.MULTI_LOCATION_REPORT}?${params.toString()}`;
    const { data } = await axios.get(url);

    if (data?.error === 0 && Array.isArray(data?.data?.list)) {
      const reportList = data.data.list;
      if (reportList?.length > 0) {
        setWashRecords(reportList);
         setOweToList(data.data.oweToList)
          setOweFromList(data.data.oweFromList)
        setTotalPages(Math.ceil(data.data.totalCount / data.data.limit) || 1);
      } else {
        setWashRecords([]);
        setOweToList([])
        setOweFromList([])
      }
    } else {
      setWashRecords([]);
      setOweToList([])
      setOweFromList([])
    }
  }

  } catch (error: any) {
    console.error("Error fetching multi-location report:", error.message);
    throw new Error(error.response?.data?.message || error.message);
  } finally {
    setLoading(false);
  }
};



  // Navigate accordung to url
  const itemClick = async (url: string) => {
    navigate(url);
  };


  const getVisiblePageNumbers = () => {
    const totalPageNumbersToShow = 5;
    let startPage = Math.max(
      1,
      currentPage - Math.floor(totalPageNumbersToShow / 2)
    );
    let endPage = startPage + totalPageNumbersToShow - 1;

    if (endPage > totalPages) {
      endPage = totalPages;
      startPage = Math.max(1, endPage - totalPageNumbersToShow + 1);
    }

    const pageNumbers = [];
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return pageNumbers;
  };

  const handlePageChange = (newPage: number) => {
    if (newPage > 0 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };


  const PlanTypes = [
    { label: "Normal Wash", value: "0" },
    { label: "Unlimited Wash", value: "1" }
  ]

const yearOptions = Array.from(
  { length: new Date().getFullYear() - 2024 + 1 },
  (_, i) => {
    const year = 2024 + i;
    return { label: year.toString(), value: year.toString() };
  }
);
  // Function to fetch company information
  const getCompany = async () => {
    try {
      const param1 = `${params.id}`;
      const { data } = carwash_owner
        ? await axios.get(CompanyApi.CARWASH_OWNER_COMPANY_DETAILS)
        : await axios.get(CompanyApi.GET_COMPANY + param1);

      // To get locations under company
      await getCompanyLocations(data.data.carWashGUID);
      // setCompanyDetails(data.data);
    } catch (error: any) {
      console.error("Error fetching company information:", error.message);
      throw new Error(error.response?.data?.message || error.message);
    }
  };

  const getCompanyLocations = async (carwashGuid: string) => {
    try {
      const { data } = carwash_owner
        ? await axios.get(LocationApi.CARWASH_OWNER_GET_COMPANY_LOCATIONS)
        : await axios.get(LocationApi.GET_COMPANY_LOCATIONS + carwashGuid);
      if (data.error === 0) {
        const location_list: LocationItem[] = data?.data;

        // Transform the array
        const transformedArray: Option[] = location_list.map((item) => ({
          label: item.locationName,
          value: item.locationId,
        }));

        setLocations(transformedArray);
      } else {
        // show error message
        toaster.toastError(data.message);
      }
    } catch (error: any) {
      throw new Error(error.response?.data?.message || error.message);
    }
  };

  const getDetails = async (UserID:any,type:any) => {
      try {
    setLoading(true);
    if(!UserID && !type)
    {
      return
    }
    setSelectedType(type)
    const params = new URLSearchParams({'userId':UserID, 
      'usageType':type
    });

    // Append only when values are present
    if (filters.locationId) params.append("locationId", filters.locationId);
    if (filters.washType) params.append("washType", filters.washType);
    if (filters.month) params.append("month", filters.month);
    if (filters.year) params.append("year", filters.year);
    if(filters.locationId!=null &&  filters.washType!=null   && filters.month!=null && filters.year!=null)
    {
    const url = `${MultiLocationApi.MULTI_LOCATION_USER_DETAILS}?${params.toString()}`;
    const { data } = await axios.get(url);

    if (data?.error === 0 && Array.isArray(data?.data)) {
      const details = data.data;
      setSelectedItem(details)
      
    } else {
      setSelectedItem([]);
    }
  }
  } catch (error: any) {
    console.error("Error fetching multi-location report:", error.message);
    throw new Error(error.response?.data?.message || error.message);
  } finally {
    setLoading(false);
  }
  }
  const handleShowPopup = async (userId: number,type:number) => {
    
    await getDetails(userId,type)
    // setSelectedItem(item);
    setShowViewPopup(true);
  
}

  const handleHidePopup = () => {
    setShowViewPopup(false);
    setSelectedItem([]);
  };

  return (
    <div className="manage-carwash-container order-list-container multi-location-wrap">
      <Container>
        <Breadcrumb>
          {carwash_owner ? (
            <>
              <Breadcrumb.Item
                onClick={() => itemClick(`/${pageURLs.dashboard}`)}
              >
                Dashboard
              </Breadcrumb.Item>
            </>
          ) : (
            <>
              <Breadcrumb.Item
                onClick={() => {
                  itemClick(`/superadmin/${pageURLs.manage_carwash}`);
                }}
              >
                Manage Car Washes
              </Breadcrumb.Item>
              <Breadcrumb.Item
                onClick={() => {
                  itemClick(
                    `/superadmin/${pageURLs.carwash_locations}/${params.id}`
                  );
                }}
              >
                {company.companyName}
              </Breadcrumb.Item>
            </>
          )}
          <Breadcrumb.Item active>Multi Location Usage Reports</Breadcrumb.Item>
        </Breadcrumb>

        <div className="manage-header-block d-flex justify-content-between align-items-center">
          <div className="search-wrap multi-location-action w-100">
            <div className="result-group d-flex align-items-center">
        
              <CWSelect
                id="selectlocation"
                name="selectlocation"
                options={locations}
                label="Select Location"
                isMandatory
                dropdownGap={0}
                dropdownPosition="auto"
                placeholder="Select Location"
                //  onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                onChange={(selected: any) => {
  const value = Array.isArray(selected) && selected.length > 0 ? selected[0].value : "";
  handleFilterChange("locationId", value.toString());
}}
  // }
              
              />
              <CWSelect
                id="selectyear"
                name="selectyear"
                options={yearOptions}
                label="Select Year"
                isMandatory
                dropdownGap={0}
                dropdownPosition="auto"
                placeholder="Select"
                 onChange={(option: any) =>
    handleFilterChange("year", option[0]?.value?.toString() || "")
  }
                //  onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                // handleFilterChange("year", e.target.value)
              // }
              />
              <CWSelect
                id="selectmonth"
                name="selectmonth"
                options={MONTHS_OPTIONS}
                label="Select Month"
                isMandatory
                dropdownGap={0}
                dropdownPosition="auto"
                placeholder="Select"
                onChange={(option: any) =>
    handleFilterChange("month", option[0]?.value?.toString() || "")
  }
              // }
              />
              <CWSelect
                id="plantype"
                name="plantype"
                options={PlanTypes}
                label="Plan Type"
                isMandatory
                dropdownGap={0}
                dropdownPosition="auto"
                placeholder="Select"
                onChange={(option: any) =>
    handleFilterChange("washType", option[0]?.value?.toString() || "")
  }
              />
        <Button
        className="secondary-btn "
        variant="primary"
        title="Run Report"
        onClick={() => {
          // Only fetch when all filters are selected
          if (
            filters.locationId &&
            filters.washType &&
            filters.month &&
            filters.year
          ) {
            setCurrentPage(1); // Reset to first page
            getMultiLocationReport(1);
          } else {
            toaster.toastError("Please select all filters before running report.");
          }
        }}
      >
        Run Report
      </Button>
 
            </div>
          </div>
        </div>
        <div className="cw-con-report-wrap">
          <h3 className="f-bold">Summary Report</h3>
          <Row>
            <Col xs={12} md={6}>
              <div className="cw-con-report-card">
                <div className="cw-con-report-cardtop">
                  <h3 className="m-0">Amount Owe from</h3>
                </div>
                 {oweFromList.length > 0 ? (
              oweFromList.map((oweFrom:any, index) => (
                <div className="cw-con-report-cardmiddle">
                  <div className="w-50">
                    <h4>{oweFrom?.LocationName}</h4>
                  </div>
                  <div className="w-50 mt-auto">
                    <h4>${oweFrom.amountOweFromOtherLocation?.toFixed(2) || 0.00}</h4>
                  </div>
                </div>
              ))
            ) : (
              <tr>
                <td colSpan={10} className="text-center">
                  {loading ? "Loading..." : "No records found"}
                </td>
              </tr>
            )}
            
              
              </div>
            </Col>
            <Col xs={12} md={6}>
              <div className="cw-con-report-card">
                <div className="cw-con-report-cardtop">
                  <h3 className="m-0">Amount Owe to</h3>
                </div>
                  {oweToList.length > 0 ? (
              oweToList.map((oweTo:any, index) => (
                <div className="cw-con-report-cardmiddle">
                  <div className="w-50">
                    {/* <h2>Amount Owe to</h2> */}
                    <h4>{oweTo?.LocationName}</h4>
                  </div>
                  <div className="w-50 mt-auto">
                    <h4>${oweTo.amountOweToOtherLocation?.toFixed(2) || 0.00}</h4>
                  </div>
                </div>
                ))
            ) : (
              <tr>
                <td colSpan={10} className="text-center">
                  {loading ? "Loading..." : "No records found"}
                </td>
              </tr>
            )}
              </div>
            </Col>

          </Row>
        </div>
        <div className="cw-table-wrap report-table">
          <Table responsive className="m-0">
            <thead>
              <tr>
                <th>User</th>
                <th>Location</th>
                <th>Subscription</th>
                <th>Total Usage</th>
                <th>Usage at Same Location</th>
                <th>Usage in Other Location</th>
                <th>Amount Owe to Other Locations</th>
                <th>Amount Owe from Other Locations</th>
              </tr>
            </thead>
            <tbody>
              {washRecords.length > 0 ? (
              washRecords.map((item, index) => (
              <tr>
                <td>{item?.FUllName}</td>
                <td>{item?.LocationName}</td>
                <td>{item?.WashName}</td>
                <td>{item?.TotalUsage}</td>
                <td>{item?.UsageAtSameLocation}</td>
                <td>{item?.UsageAtOtherLocation}</td>
                <td>
                  ${item?.AmountOweToOtherLocation?.toFixed(2) || 0.00} <br />
                  {item?.AmountOweToOtherLocation >0 ?<Link
                    to={""}
                    onClick={() => handleShowPopup(item?.UserID,1)}
                    className="view-detail"
                  >
                    View Details
                  </Link>:""}
                </td>
                <td>
                  ${item?.AmountOweFromOtherLocation?.toFixed(2) || 0.00} <br />
                  {item?.AmountOweFromOtherLocation > 0 ? <Link to={""} 
                   onClick={() => handleShowPopup(item?.UserID,0)}
                  className="view-detail">
                    View Details
                  </Link>:""}
                </td>
              </tr>
               ))
            ) : (
              <tr>
                <td colSpan={10} className="text-center">
                  {loading ? "Loading..." : "No records found"}
                </td>
              </tr>
            )}
              
            </tbody>
          </Table>

          {washRecords && washRecords?.length ? (
            <div className="mt-3 d-flex justify-content-end">
              <Pagination>
                <Pagination.First
                  disabled={currentPage === 1}
                  onClick={() => handlePageChange(1)}
                />
                <Pagination.Prev
                  disabled={currentPage === 1}
                  onClick={() => handlePageChange(currentPage - 1)}
                />
                {getVisiblePageNumbers().map((pageNumber) => (
                  <Pagination.Item
                    key={pageNumber}
                    active={pageNumber === currentPage}
                    onClick={() => handlePageChange(pageNumber)}
                  >
                    {pageNumber}
                  </Pagination.Item>
                ))}
                <Pagination.Next
                  disabled={currentPage === totalPages}
                  onClick={() => handlePageChange(currentPage + 1)}
                />
                <Pagination.Last
                  disabled={currentPage === totalPages}
                  onClick={() => handlePageChange(totalPages)}
                />
              </Pagination>
            </div>
          ) : null}
        </div>
      </Container>

      {loading && <SmallLoader />}
      <Modal
        show={showViewPopup}
        onHide={handleHidePopup}
        centered
        className="location-modal"
      >
        <Modal.Header closeButton></Modal.Header>
        <Modal.Body>
          <div className="cw-con-report-card">
            <div className="cw-con-report-cardtop">
              <h3 className="m-0">{selectedType==0 ? "Amount Owe From" :"Amount Owe to"}</h3>
            </div>
             {selectedItem && selectedItem?.length > 0 ? (
              selectedItem?.map((item, index) => (
            <div className="cw-con-report-cardmiddle">
              <div className="w-50">
                <h4>{item?.LocationName}</h4>
              </div>
              <div>
                <h4>${item?.Amount?.toFixed(2)}</h4>
              </div>
            </div>
              ))):
          
                
                  loading ? "Loading..." : "No records found"
             
           
            }
         
           
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default MultilocationUsageReports;
